import { Navigate, useLocation } from 'react-router-dom'
import { ReactNode } from 'react'
import { isSigningOut, useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: 'admin' | 'instructor' | 'student'
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, token, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  if (!token || !user) {
    // A sign-out reaches here too — logout clears the session, React re-renders,
    // and only then does the browser leave for the new document. Recording a
    // return url in that window hands the current user's page to whoever signs
    // in next, so say nothing and let the pending navigation happen.
    if (isSigningOut()) return null

    // Preserve the current URL so login can redirect back
    const returnUrl = encodeURIComponent(location.pathname + location.search + location.hash)
    // Also save to sessionStorage as backup (survives page reloads)
    sessionStorage.setItem('loginReturnUrl', location.pathname + location.search + location.hash)
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') {
    // Admin can access all routes
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

